import { Controller } from "@hotwired/stimulus"

// Controller for handling video adding in Trix editor
export default class extends Controller {
  static get targets() {
    return ["videoUrl", "template", "thumbnail", "thumbnailLink", "thumbnailContainer", "thumbnailAlert", "thumbnailSrc", "thumbnailLoader", "insertButton", "previewSectionTitle"]
  }

  static get values() {
    return {
      playIconUrl: String,
      attachmentId: Number,
      followUpId: Number
    }
  }

  showModal({ detail: { followUpId, attachmentId, videoUrl, thumbnailUrl } }) {
    this.followUpIdValue = followUpId
    this.attachmentIdValue = attachmentId
    if (videoUrl) {
      this.videoUrlTarget.value = videoUrl
    }
    if (thumbnailUrl) {
      this.thumbnailSrcTarget.value = thumbnailUrl
    }
    $(this.element).modal("show")
    if (videoUrl || thumbnailUrl) {
      this.fetchAndShowThumbnail()
    }
  }

  async fetchAndShowThumbnail() {
    this.insertButtonTarget.disabled = true
    this.thumbnailContainerTarget.classList.add("d-none")
    this.thumbnailTarget.classList.add("d-none")
    this.previewSectionTitleTarget.classList.add("d-none")

    let videoUrl = null
    try {
      videoUrl = new URL(this.videoUrlTarget.value)
    } catch (error) {
      this._hideLoadingSpinner()
      return
    }

    this._showLoadingSpinner()

    if (this._providerHandledBasedOnUrl(videoUrl)) {
      let thumbnailUrl = await this._getThumbnailForVideoUrl(videoUrl)

      this.renderThumbnail(thumbnailUrl)
    } else {
      App.analytics.ga_track("Video block using unsupported provider", videoUrl.hostname)

      this.thumbnailContainerTarget.classList.remove("d-none")
      this.thumbnailTarget.classList.add("d-none")
      this.previewSectionTitleTarget.classList.add("d-none")

      if (this.thumbnailSrcTarget.value !== "") {
        this.renderThumbnail(this.thumbnailSrcTarget.value)
      } else {
        this._hideLoadingSpinner()
      }
    }
  }

  // Because there are no out of the box events for background images, we mimic the behaviour by creating in memory
  // image and listening to its load and error events
  renderThumbnail(thumbnailUrl) {
    this.thumbnailLinkTarget.href = this.videoUrlTarget.value
    let preloaderImg = new Image()
    preloaderImg.src = thumbnailUrl

    preloaderImg.addEventListener("load", () => {
      this._setThumbnailBackgroundImage(true, thumbnailUrl)
      this.insertButtonTarget.disabled = false
      preloaderImg = null
    })
    preloaderImg.addEventListener("error", () => {
      // Below check is to make sure that we don't hide thumbnail when user is typing in the video url
      if (this.thumbnailSrcTarget.value === thumbnailUrl) {
        this._setThumbnailBackgroundImage(false, thumbnailUrl)
      }
      preloaderImg = null
    })
  }

  _setThumbnailBackgroundImage(isSuccess, thumbnailUrl) {
    if (isSuccess) {
      this.thumbnailTarget.dataset.src = thumbnailUrl
      this.thumbnailTarget.style.backgroundImage = `url("${thumbnailUrl}")`
      this.thumbnailTarget.classList.remove("d-none")
      this.previewSectionTitleTarget.classList.remove("d-none")
      this.insertButtonTarget.disabled = false
    } else {
      this.thumbnailTarget.dataset.src = null
      this.thumbnailTarget.style.backgroundImage = `url("")`
      this.thumbnailTarget.classList.add("d-none")
      this.previewSectionTitleTarget.classList.add("d-none")
      this.insertButtonTarget.disabled = true
    }
    this._hideLoadingSpinner()
  }

  addVideoToEditor() {
    let thumbnailUrl = this.thumbnailTarget.dataset.src
    let thumbnailHtml = this.templateTarget.innerHTML
    thumbnailHtml = thumbnailHtml.replace(/THUMBNAIL_URL/g, thumbnailUrl)
    thumbnailHtml = thumbnailHtml.replace(/VIDEO_URL/g, this.videoUrlTarget.value)

    this.dispatch("trix-video-add", {
      detail: {
        followUpId: this.followUpIdValue,
        thumbnailHtml: thumbnailHtml,
        videoUrl: this.videoUrlTarget.value,
        thumbnailUrl: thumbnailUrl
      }
    })
    this.resetFields()
    $(this.element).modal("hide")
  }

  resetFields() {
    this.videoUrlTarget.value = ""
    this.thumbnailTarget.src = ""
    this.thumbnailSrcTarget.value = ""
    this.insertButtonTarget.disabled = true
    this.followUpIdValue = null
    this.thumbnailTarget.classList.add("d-none")
    this.thumbnailContainerTarget.classList.add("d-none")
    this.thumbnailTarget.classList.add("d-none")
    this.previewSectionTitleTarget.classList.add("d-none")
  }

  _showLoadingSpinner() {
    this.thumbnailLoaderTarget.classList.remove("d-none")
  }

  _hideLoadingSpinner() {
    this.thumbnailLoaderTarget.classList.add("d-none")
  }

  async _getThumbnailForVideoUrl(videoUrl) {
    let hostname = videoUrl.hostname

    return new Promise(resolve => {
      if (hostname.includes("youtube")) {
        let videoId
        if (videoUrl.pathname.includes("shorts")) {
          let parts = videoUrl.pathname.split("/")
          if (parts.length == 3) { videoId = parts.pop() }
        } else {
          videoId = videoUrl.searchParams.get("v")
        }

        if (videoId == null) {
          this._fallbackToUserThumbnail()
          return
        }

        resolve(`https://img.youtube.com/vi/${videoId}/0.jpg`)
      } else if (hostname.includes("youtu.be")) {
        let videoId = videoUrl.pathname.split("/").pop()
        if (videoId == null) {
          this._fallbackToUserThumbnail()
          return
        }

        resolve(`https://img.youtube.com/vi/${videoId}/0.jpg`)
      } else if (hostname.includes("loom")) {
        let videoId = videoUrl.pathname.split("/").pop()
        if (videoId == null) {
          this._fallbackToUserThumbnail()
          return
        }

        // We try to get thumbnail from the Loom API oEmbed since Loom allows custom thumbnails under different URLs
        // than normal video thumbnails. If we won't be able to get it, we will use default video thumbnail.
        fetch(`https://www.loom.com/v1/oembed?url=${videoUrl}`)
          .then(response => { return response.json() })
          .then(data => {
            resolve(data.thumbnail_url)
          })
          .catch(() => {
            resolve(`https://cdn.loom.com/sessions/thumbnails/${videoId}-00001.jpg`)
          })
      } else if (hostname.includes("vidyard")) {
        let videoId = videoUrl.pathname.split("/").pop()
        if (videoId == null) {
          this._fallbackToUserThumbnail()
          return
        }

        resolve(`https://play.vidyard.com/${videoId}.jpg`)
      } else if (hostname.includes("vimeo")) {
        //   We need to get the thumbnail from the Vimeo API oEmbed
        fetch(`https://vimeo.com/api/oembed.json?url=${videoUrl.href}`)
          .then(response => { return response.json() })
          .then(data => {
            resolve(data.thumbnail_url)
          })
          .catch(() => {
            this._fallbackToUserThumbnail()
          })
      }
    })
  }

  _providerHandledBasedOnUrl(url) {
    return /.*(youtube.com|loom.com|share.vidyard.com|vimeo.com|youtu.be).*/.test((url.host))
  }

  _fallbackToUserThumbnail() {
    this._hideLoadingSpinner()
    this.thumbnailContainerTarget.classList.remove("d-none")
    this.thumbnailTarget.classList.add("d-none")
    this.previewSectionTitleTarget.classList.add("d-none")
  }
}
